import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import {
  cartHasPaidPromoGifts,
  clearGiftCouponConflict,
  getPromoConflictMessageFromDom,
  isPromoGiftLine,
  removePaidPromoGifts,
  scheduleGiftCouponConflictRestore,
  setGiftCouponConflict,
  syncGiftCouponConflictUI,
} from '@theme/cart-promo-gift-sync';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorGiftConflict - The gift combination conflict error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'cartDiscountErrorGiftConflict',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    // Restaurar aviso si el cliente no lo cerró (sobrevive morph/refresh).
    scheduleGiftCouponConflictRestore();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Regalos que estaban a $0 (descuento de producto aplicado).
   * @param {object} cart
   * @returns {{ key: string, variantId: number|string }[]}
   */
  #snapshotFreeGifts(cart) {
    return (cart?.items || [])
      .filter((item) => isPromoGiftLine(item) && Number(item.final_line_price) === 0)
      .map((item) => ({ key: item.key, variantId: item.variant_id }));
  }

  /**
   * Shopify quitó el descuento del regalo al aplicar un cupón no combinable.
   * @param {object} cartAfter
   * @param {{ key: string, variantId: number|string }[]} freeGiftsBefore
   * @returns {boolean}
   */
  #giftsLostFreeDiscount(cartAfter, freeGiftsBefore) {
    if (!freeGiftsBefore?.length) return false;

    return freeGiftsBefore.some((gift) => {
      const line = (cartAfter?.items || []).find(
        (item) =>
          item.key === gift.key ||
          (isPromoGiftLine(item) && String(item.variant_id) === String(gift.variantId))
      );
      return Boolean(line && Number(line.final_line_price) > 0);
    });
  }

  /**
   * @returns {Promise<object|null>}
   */
  async #fetchCart() {
    const cartPath = Theme.routes.cart_url || '/cart';
    const cartUrl = cartPath.endsWith('.js') ? cartPath : `${cartPath}.js`;
    const response = await fetch(cartUrl);
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorGiftConflict } =
      this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      // No ocultar el aviso de conflicto regalo/cupón aquí:
      // permanece visible hasta que el cliente lo cierre con la X.

      const cartBefore = await this.#fetchCart();
      const freeGiftsBefore = this.#snapshotFreeGifts(cartBefore);

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Caso: Shopify rechaza el cupón y mantiene el regalo.
      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      // Caso: cupón deja el regalo cobrando (con o sin transición 0 → precio).
      // También cubre SKUs de regalo sin property `_promo_gift`.
      if (this.#giftsLostFreeDiscount(data, freeGiftsBefore) || cartHasPaidPromoGifts(data)) {
        const conflictMessage =
          this.dataset.conflictMessage?.trim() || getPromoConflictMessageFromDom();

        setGiftCouponConflict(true, conflictMessage);
        await removePaidPromoGifts({ refresh: true, markConflict: true });

        discountCode.value = '';

        // Re-fetch section HTML with coupon kept and gift removed.
        const refreshConfig = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(','),
            sections: [this.dataset.sectionId],
          }),
        });
        const refreshResponse = await fetch(Theme.routes.cart_update_url, {
          ...refreshConfig,
          signal: abortController.signal,
        });
        const refreshData = await refreshResponse.json();

        document.dispatchEvent(new DiscountUpdateEvent(refreshData, this.id));
        morphSection(this.dataset.sectionId, refreshData.sections[this.dataset.sectionId]);
        // Mobile: morph async de secciones puede ocultar el aviso; reintentar varios frames.
        scheduleGiftCouponConflictRestore();
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event on the remove button.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const removeButton = event.target.closest('.cart-discount__pill-remove');
    if (!(removeButton instanceof HTMLButtonElement) || typeof this.dataset.sectionId !== 'string') {
      return;
    }

    const pill = removeButton.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const stillHasCoupons = (data.discount_codes || []).some(
        (discount) => discount?.applicable !== false && discount?.code
      );

      // Limpiar conflicto ANTES del evento, para que el listener de discount
      // pueda re-agregar el regalo sin bloqueo.
      if (!stillHasCoupons) {
        clearGiftCouponConflict();
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);

      if (stillHasCoupons) {
        scheduleGiftCouponConflictRestore();
      }
      // Si no hay cupones, el listener de discountUpdate se encarga del auto-add.
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'gift_conflict'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorGiftConflict,
    } = this.refs;

    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    cartDiscountErrorGiftConflict.classList.add('hidden');

    const target =
      type === 'shipping'
        ? cartDiscountErrorShipping
        : type === 'gift_conflict'
          ? cartDiscountErrorGiftConflict
          : cartDiscountErrorDiscountCode;

    if (type === 'gift_conflict') {
      const message = this.dataset.conflictMessage?.trim() || getPromoConflictMessageFromDom();
      if (message) target.textContent = message;
    }

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
